import React, { Component } from 'react';
import _debounce from 'lodash.debounce';
import memoizeOne from 'memoize-one';
import cx from 'classnames';
import { credentials } from '@shootsta/client-auth';
import { Tabs, Input, FormModal, Text } from '../../../../../ui-kit';

import ConfirmationModal from '../../../../ConfirmationModal';
import MembersTab from './MembersTab';
import AddUsersTab from './AddUsersTab';
import AddTeamsTab from './AddTeamsTab';
import '../styles/_collaboration-modal.scss';



























const TEAM_FIELDS_TO_FILTER = ['name'];
const USER_FIELDS_TO_FILTER = ['firstName', 'lastName', 'email'];

function itemMatchesFilter(item, filter) {
  const itemKeys = Object.keys(item);
  const fields = itemKeys.includes('teamId') ? TEAM_FIELDS_TO_FILTER : USER_FIELDS_TO_FILTER;

  return itemKeys.filter((key) => fields.includes(key)).
  some((itemKey) => {
    const itemField = item[itemKey];

    return itemField &&
    typeof itemField === 'string' &&
    itemField.toLowerCase().includes(filter.toLowerCase());
  }
  );
}

export default class CollaborationModal extends
Component {


  onSearchChange = _debounce(async ({ target: { value } }) => {
    if (!value) {
      return void this.setState({ users: void 0, teams: void 0 });
    }

    this.setState({ loading: true });

    const { selectedTabIndex } = this.state;

    const { getUsersByName, getTeamsByName, organisationId } = this.props;

    const { data, error } = selectedTabIndex === 1 ?
    await getUsersByName({ name: value, organisationId }) :
    await getTeamsByName({ name: value, organisationId });

    if (!data || error) {return void this.setState({ loading: false });}

    this.setState({
      users: data.getUsersByName,
      teams: data.getTeamsByName,
      loading: false
    });
  }, 250);

  filterByName = memoizeOne(
    (filter, list) => list.filter((item) => itemMatchesFilter(item, filter)),
    ([newFilter, newList], [oldFilter, oldList]) => newFilter === oldFilter && newList === oldList
  );

  constructor(props) {
    super(props);

    this.state = {
      teams: void 0,
      users: void 0,
      loading: false,
      selectedTabIndex: 0,
      filter: '',
      showOwnerConfirm: false,
      showOwnerConfirmStep2: false,
      pendingOwnerChange: null,
      dropdownResetKey: 0
    };
  }

  updateSelectedTabIndex = ({ selectedTabIndex }) => {
    this.setState({ selectedTabIndex, filter: '', users: void 0, teams: void 0, loading: false });

    if (!this._searchInputRef) {return;}

    this._searchInputRef.clearValue();
  };

  handleInputChange = ({ target: { value } }) => {
    this.setState({ filter: value });
  };

  setSearchInputRef = (ref) => {
    if (this._searchInputRef || !ref) {
      return;
    }

    this._searchInputRef = ref;
  };

  getSearchPlaceholder = () => {
    const { selectedTabIndex } = this.state;

    if (selectedTabIndex === 0) {return 'Search current members';}

    return `Search ${selectedTabIndex === 1 ? 'users' : 'teams'}`;
  };

  handleUpdateMember = (draftMemberId, updatedItem) => {
    const { onUpdateMember, members } = this.props;

    if (updatedItem.role?.name === 'OWNER') {
      const currentOwner = (members || []).find((m) => m.role?.name === 'OWNER');
      if (currentOwner) {
        this.setState({
          pendingOwnerChange: { draftMemberId, updatedItem, currentOwner },
          showOwnerConfirm: true
        });
        return;
      }
    }
    onUpdateMember(draftMemberId, updatedItem);
  };

  executeOwnerTransfer = () => {
    const { onUpdateMember, roles } = this.props;
    const { pendingOwnerChange } = this.state;
    if (!pendingOwnerChange) return;
    const { draftMemberId, updatedItem, currentOwner } = pendingOwnerChange;
    const collaboratorRole = roles.find((r) => r.name === 'COLLABORATOR');

    onUpdateMember(draftMemberId, updatedItem);
    onUpdateMember(currentOwner.draftMemberId, { ...currentOwner, role: collaboratorRole });
  };

  confirmOwnerTransfer = () => {
    const currentUserId = this.getCurrentUserId();
    const { pendingOwnerChange } = this.state;
    const isCurrentUserOwner = pendingOwnerChange?.currentOwner?.userId === currentUserId;

    if (isCurrentUserOwner) {
      this.setState({ showOwnerConfirm: false, showOwnerConfirmStep2: true });
    } else {
      this.executeOwnerTransfer();
      this.setState({ showOwnerConfirm: false, pendingOwnerChange: null });
    }
  };

  confirmOwnerTransferStep2 = () => {
    this.executeOwnerTransfer();
    this.setState({ showOwnerConfirmStep2: false, pendingOwnerChange: null });
  };

  resetOwnerTransferState = (flagToReset) => {
    this.setState((prevState) => ({
      [flagToReset]: false,
      pendingOwnerChange: null,
      dropdownResetKey: prevState.dropdownResetKey + 1
    }));
  };

  cancelOwnerTransfer = () => {
    this.resetOwnerTransferState('showOwnerConfirm');
  };

  cancelOwnerTransferStep2 = () => {
    this.resetOwnerTransferState('showOwnerConfirmStep2');
  };

  getCurrentUserId = () => {
    const { user: { userId = '' } = {} } = credentials.get() || {};
    return userId;
  };

  canSave = () => {
    const { members } = this.props;

    if (!members) {return false;}

    const ownersCount = members.reduce(
      (acc, member) => member.role && member.role.name && member.role.name === 'OWNER' ? acc + 1 : acc,
      0
    );
    return ownersCount === 1;
  };

  render() {
    const {
      members,
      onUpdateAndSaveMembers,
      onClose,
      onRemoveMember,
      onAddMember,
      visible,
      roles
    } = this.props;

    const {
      selectedTabIndex,
      users,
      teams,
      filter,
      loading,
      showOwnerConfirm,
      pendingOwnerChange,
      showOwnerConfirmStep2,
      dropdownResetKey
    } = this.state;
    const currentUserId = this.getCurrentUserId();
    const isCurrentUserOwner = pendingOwnerChange?.currentOwner?.userId === currentUserId;

    const filteredMembers = filter ? this.filterByName(filter, members) : members || [];
    const membersIds = members ?
    members.map(({ userId, teamId }) => userId || teamId).filter(Boolean) :
    [];
    const canSave = this.canSave();

    return (
      <div className={cx('collaboration-modal', { 'collaboration-modal--loading': loading })}>
        <FormModal
          visible={visible}
          onClose={onClose}
          saveText="Save Changes"
          saveDisabled={!canSave}
          onSave={onUpdateAndSaveMembers}
          title="Edit Collaborators"
          subtitle="Choose who you want to collaborate with you">

          <div className="collaboration-modal__body">
            <Input
              ref={this.setSearchInputRef}
              className="collaboration-modal__search"
              prefix="search"
              placeholder={this.getSearchPlaceholder()}
              value={filter}
              onChange={selectedTabIndex === 0 ? this.handleInputChange : this.onSearchChange} />

            <Tabs updateSelectedTabIndex={this.updateSelectedTabIndex}>
              <MembersTab
                key={dropdownResetKey}
                title={`Members (${filteredMembers.length})`}
                members={filteredMembers}
                roles={roles}
                onRemoveMember={onRemoveMember}
                onUpdateMember={this.handleUpdateMember} />

              <AddUsersTab
                title="Add Users"
                onAddMember={onAddMember}
                users={users}
                membersIds={membersIds}
                loading={loading} />

              <AddTeamsTab
                title="Add Teams"
                onAddMember={onAddMember}
                teams={teams}
                membersIds={membersIds}
                loading={loading} />

            </Tabs>
            {!canSave &&
            <Text warning>There can only be one user with the Owner role.</Text>}

          </div>
        </FormModal>
        <ConfirmationModal
          visible={showOwnerConfirm}
          onClose={this.cancelOwnerTransfer}
          onSave={this.confirmOwnerTransfer}
          title="Transfer Ownership"
          saveBtnText="Transfer Ownership"
          hideSaveBtn={!isCurrentUserOwner}>

          <Text className="pb-4">There can only be one user with the Owner role.</Text>
        </ConfirmationModal>
        <ConfirmationModal
          visible={showOwnerConfirmStep2}
          onClose={this.cancelOwnerTransferStep2}
          onSave={this.confirmOwnerTransferStep2}
          title="Transfer Ownership"
          saveBtnText="Confirm Transfer">

          <Text className="pb-4">
            Are you sure? This means you will no longer be the owner of this project.
          </Text>
        </ConfirmationModal>
      </div>);

  }
}